import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { Store } from "lucide-react";

/**
 * GeM Support request dialog. Triggered from the CRM Reporting Log Report flow
 * for any client. Submits to POST /api/gem-requests. The created request flows
 * into the GeM Work module's "User Request" / "My Raised" tabs.
 */
export default function GeMSupportDialog({ open, onOpenChange, client }) {
  const [needsHelp, setNeedsHelp] = useState(true);
  const [option, setOption] = useState("Direct Purchase");
  const [optionOther, setOptionOther] = useState("");
  const [orderKind, setOrderKind] = useState("Product");
  const [productDesc, setProductDesc] = useState("");
  const [serviceDesc, setServiceDesc] = useState("");
  const [orderValue, setOrderValue] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setNeedsHelp(true);
    setOption("Direct Purchase");
    setOptionOther("");
    setOrderKind("Product");
    setProductDesc("");
    setServiceDesc("");
    setOrderValue("");
  };

  const submit = async () => {
    if (!client?.id) {
      toast.error("Select a client first");
      return;
    }
    if (option === "Other" && !optionOther.trim()) {
      toast.error("Please describe the GeM option");
      return;
    }
    if (orderKind === "Product" && !productDesc.trim()) {
      toast.error("Please describe the product");
      return;
    }
    if (orderKind === "Service" && !serviceDesc.trim()) {
      toast.error("Please describe the service");
      return;
    }
    setBusy(true);
    try {
      await api.post("/gem-requests", {
        client_id: client.id,
        needs_help: needsHelp,
        gem_option: option,
        gem_option_other: optionOther,
        order_kind: orderKind,
        product_description: productDesc,
        service_description: serviceDesc,
        order_value: Number(orderValue || 0),
      });
      toast.success("GeM Support request raised ✓");
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="gem-support-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2"><Store size={18} className="text-indigo-600" /> GeM Support Request</DialogTitle>
          <div className="text-[11px] text-slate-500 mt-1">{client?.client_name} · U-DISE {client?.u_dise || "—"}</div>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <div>
              <div className="text-sm font-semibold text-slate-800">Do they need help placing an order via GeM?</div>
              <div className="text-[11px] text-slate-500">Toggle off if they only need consulting / no actual order placement.</div>
            </div>
            <Switch checked={needsHelp} onCheckedChange={setNeedsHelp} data-testid="gem-needs-help" />
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">GeM Option *</Label>
            <select
              value={option}
              onChange={(e) => setOption(e.target.value)}
              className="mt-1.5 w-full h-10 text-sm border border-slate-200 rounded-md px-3 bg-white dark:bg-slate-900 dark:border-slate-700"
              data-testid="gem-option"
            >
              <option value="Direct Purchase">Direct Purchase</option>
              <option value="BOQ BID">BOQ BID</option>
              <option value="Custom BID">Custom BID</option>
              <option value="Other">Other (specify)</option>
            </select>
            {option === "Other" && (
              <Input
                value={optionOther}
                onChange={(e) => setOptionOther(e.target.value)}
                placeholder="Describe the option"
                className="mt-2 h-9 text-sm"
                data-testid="gem-option-other"
              />
            )}
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Type of Order *</Label>
            <div className="mt-1.5 flex gap-2">
              {["Product", "Service"].map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setOrderKind(k)}
                  className={`flex-1 h-10 text-sm font-semibold rounded-md border-2 transition-all ${orderKind === k ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"}`}
                  data-testid={`gem-kind-${k.toLowerCase()}`}
                >
                  {k}
                </button>
              ))}
            </div>
          </div>

          {orderKind === "Product" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">What Product Are You Selling? *</Label>
              <Input
                value={productDesc}
                onChange={(e) => setProductDesc(e.target.value)}
                placeholder="e.g. 30 desktop computers + projector"
                className="mt-1.5 h-9 text-sm"
                data-testid="gem-product-desc"
              />
            </div>
          )}
          {orderKind === "Service" && (
            <div>
              <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">What Service Are You Selling? *</Label>
              <Input
                value={serviceDesc}
                onChange={(e) => setServiceDesc(e.target.value)}
                placeholder="e.g. Annual maintenance for ATL lab"
                className="mt-1.5 h-9 text-sm"
                data-testid="gem-service-desc"
              />
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Order Value (₹) *</Label>
            <Input
              type="number"
              value={orderValue}
              onChange={(e) => setOrderValue(e.target.value)}
              placeholder="0"
              className="mt-1.5 h-9 text-sm"
              data-testid="gem-order-value"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="gem-support-submit">
            {busy ? "Submitting…" : "Raise Request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
